import React, { useCallback, useEffect, useState } from 'react';
import { FaTimes } from 'react-icons/fa';

type Props = {
  message: string;
  show: boolean; // Changed from 'visible' to match your provider
  duration?: number; // ms
  onClose?: () => void;
  type?: 'success' | 'error' | 'warning' | 'info'; // Added type support
  title?: string;
};

const AutoDismissMessage: React.FC<Props> = ({ 
  message, 
  show, 
  duration = 5000, 
  onClose, 
  type = 'success',
  title 
}) => {
  const [visible, setVisible] = useState<boolean>(show);
  const [progress, setProgress] = useState<number>(100);
  const [isPaused, setIsPaused] = useState<boolean>(false);

  useEffect(() => {
    setVisible(show);
    if (show) {
      setProgress(100);
      
      if (duration > 0) {
        const totalTime = duration;
        const intervalTime = 50;
        const decrement = (100 / (totalTime / intervalTime));
        
        const interval = setInterval(() => {
          if (!isPaused) {
            setProgress(prev => Math.max(prev - decrement, 0));
          }
        }, intervalTime);

        return () => clearInterval(interval);
      }
    }
  }, [show, duration, isPaused]);

  const handleClose = useCallback(() => {
    setVisible(false);
    onClose && onClose();
  }, [onClose]);

  useEffect(() => {
    if (show && visible && progress <= 0) {
      handleClose();
    }
  }, [progress, show, visible, handleClose]);

  const handleMouseEnter = () => setIsPaused(true);
  const handleMouseLeave = () => setIsPaused(false);

  // Type configurations
  const typeConfigs = {
    success: {
      bgColor: 'bg-green-50',
      borderColor: 'border-green-200',
      textColor: 'text-green-800',
      titleColor: 'text-green-900',
      progressColor: 'bg-green-500',
      iconColor: 'text-green-600',
    },
    error: {
      bgColor: 'bg-red-50',
      borderColor: 'border-red-200',
      textColor: 'text-red-800',
      titleColor: 'text-red-900',
      progressColor: 'bg-red-500',
      iconColor: 'text-red-600',
    },
    warning: {
      bgColor: 'bg-amber-50',
      borderColor: 'border-amber-200',
      textColor: 'text-amber-800',
      titleColor: 'text-amber-900',
      progressColor: 'bg-amber-500',
      iconColor: 'text-amber-600',
    },
    info: {
      bgColor: 'bg-blue-50',
      borderColor: 'border-blue-200',
      textColor: 'text-blue-800',
      titleColor: 'text-blue-900',
      progressColor: 'bg-blue-500',
      iconColor: 'text-blue-600',
    },
  };

  const config = typeConfigs[type];

  if (!visible) return null;

  return (
    <div 
      className="fixed top-4 left-1/2 transform -translate-x-1/2 z-50 max-w-md w-full"
      style={{
        opacity: visible ? 1 : 0,
        transform: visible ? 'translate(-50%, 0)' : 'translate(-50%, -10px)',
        transition: 'opacity 300ms, transform 300ms',
      }}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      role="alert"
      aria-live="polite"
    >
      <div className={`rounded-xl shadow-lg border ${config.borderColor} ${config.bgColor} overflow-hidden`}>
        {/* Progress bar */}
        {duration > 0 && (
          <div className="h-1 w-full bg-gray-200">
            <div
              className={`h-full ${config.progressColor}`}
              style={{ 
                width: `${progress}%`,
                transition: 'width 50ms linear'
              }}
            />
          </div>
        )}

        <div className="p-4">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              {title && (
                <h4 className={`text-sm font-semibold ${config.titleColor} mb-1`}>
                  {title}
                </h4>
              )}
              <p className={`text-sm ${config.textColor}`}>
                {message}
              </p>
            </div>
            
            <button
              onClick={handleClose}
              className={`flex-shrink-0 ml-3 ${config.textColor} hover:opacity-70 transition-opacity focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-current`}
              aria-label="Close notification"
            >
              <FaTimes className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AutoDismissMessage;
